import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import PortfolioLayout from '@/layouts/PortfolioLayout'
import AccountSettingsNav from '@/components/AccountSettingsNav'
import { HelpTooltip } from '@/components/HelpTooltip'
import { useBrand } from '@/hooks/useBrand'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import ReconnectCredentialModal from '@/components/account/ReconnectCredentialModal'
import SwapCredentialModal, { type SwapCandidate } from '@/components/account/SwapCredentialModal'
import RemoveConnectionDialog from '@/components/account/RemoveConnectionDialog'
import ConnectionRecoveryPanel, { ConnectionHealedPanel } from '@/components/account/ConnectionRecoveryPanel'
import ConnectionCheckMeter, {
  budgetExhausted,
  budgetExhaustedMessage,
  type AuthBudget,
} from '@/components/account/ConnectionCheckMeter'
import {
  Plug,
  AlertTriangle,
  ArrowLeft,
  ArrowRightLeft,
  KeyRound,
  CheckCircle2,
  XCircle,
  Loader2,
  RotateCw,
  ShieldCheck,
  Lock,
  Trash2,
  Building2,
} from 'lucide-react'
import { formatShortWhen } from '@/lib/account-dates'
import { apiFetch, apiErrorMessage } from '@/lib/csrf'
import type { AccountSettingsSharedProps } from '@/types/shared'

interface ConnectionProperty {
  id: number
  name: string
  /** This hotel's canonical path (Operator::AccountSettingsController#pms_connection_rows). */
  basePath: string
  lastStructureSyncAt: string | null
  lastPolledAt: string | null
  errorMessage: string | null
  errorAt: string | null
}

type HealthStatus = 'healthy' | 'degraded' | 'suspended'

interface PmsConnection {
  id: number
  name: string
  environment: string
  clientIdMasked: string
  healthStatus: HealthStatus
  consecutiveAuthFailures: number
  suspendedAt: string | null
  lastProbeAt: string | null
  nextProbeEstimate: string | null
  /** False when another business owns it: this business may TEST it, never change it. */
  ownedByAccount: boolean
  /** GLOBAL property count — a connection can look empty here and still serve another business. */
  propertyCount: number
  /** Null when the hourly-check meter is unreadable. Never a reason to block an action. */
  authBudget: AuthBudget | null
  properties: ConnectionProperty[]
}

interface FromProperty {
  id: number
  name: string
  /**
   * Where "Back to <hotel>" goes. The connections page is account-level with no property in
   * scope, so the deep-linked property's path can only come from the backend
   * (Operator::AccountSettingsController#from_property_summary).
   */
  basePath: string
}

interface PmsConnectionsProps extends AccountSettingsSharedProps {
  connections: PmsConnection[]
  fromProperty?: FromProperty | null
  canManageConnections: boolean
  // False on the real production deployment — see EviivoCredential.staging_selectable?
  allowStagingEnvironment: boolean
}


// Health badge: emerald / amber / rose to match the ActiveAdmin credential-health colours and
// the design-system semantic colours (positive / warning / negative).
const HEALTH_META: Record<HealthStatus, { label: string; className: string; Icon: typeof ShieldCheck }> = {
  healthy: { label: 'Connected', className: 'border-emerald-200 bg-emerald-50 text-emerald-700', Icon: ShieldCheck },
  degraded: { label: 'Reconnecting', className: 'border-amber-200 bg-amber-50 text-amber-700', Icon: RotateCw },
  suspended: { label: 'Needs attention', className: 'border-rose-200 bg-rose-50 text-rose-700', Icon: AlertTriangle },
}

type TestResult = { status: 'testing' | 'ok' | 'fail'; message?: string }

export default function PmsConnections({
  currentUser,
  currentAccount,
  accountAbilities,
  activeSection,
  connections,
  fromProperty,
  canManageConnections,
  allowStagingEnvironment,
}: PmsConnectionsProps) {
  const brand = useBrand()
  const [reconnectTarget, setReconnectTarget] = useState<PmsConnection | null>(null)
  const [swapTarget, setSwapTarget] = useState<PmsConnection | null>(null)
  const [removeTarget, setRemoveTarget] = useState<PmsConnection | null>(null)
  const [testResults, setTestResults] = useState<Record<number, TestResult>>({})
  // A HEAL is not a transient chip. It is the end of an outage, so it stays on the card that
  // was broken until the operator dismisses it — see ConnectionHealedPanel.
  const [healed, setHealed] = useState<Record<number, string>>({})

  function clearResultSoon(connectionId: number) {
    window.setTimeout(() => {
      setTestResults((prev) => {
        const next = { ...prev }
        delete next[connectionId]
        return next
      })
    }, 8000)
  }

  // "Test connection" is no longer only a diagnostic: a passing test on a broken connection
  // HEALS it (and restarts its properties' syncing). So the two passing outcomes are reported
  // very differently — an ordinary pass is a transient chip, a heal is a resolution.
  async function testConnection(connection: PmsConnection) {
    setTestResults((prev) => ({ ...prev, [connection.id]: { status: 'testing' } }))
    try {
      const response = await apiFetch(`${currentAccount.settingsPath}/pms-connections/${connection.id}/test`, { method: 'POST' })
      const data = await response.json()

      if (!response.ok) {
        setTestResults((prev) => ({
          ...prev,
          [connection.id]: { status: 'fail', message: data.errors?.join(', ') || 'Test failed.' },
        }))
        clearResultSoon(connection.id)
        return
      }

      if (data.ok && data.healed) {
        // Surface the server's own words, and re-fetch so the card actually turns healthy.
        setHealed((prev) => ({ ...prev, [connection.id]: data.message || 'Connection restored.' }))
        setTestResults((prev) => {
          const next = { ...prev }
          delete next[connection.id]
          return next
        })
        router.reload({ only: ['connections'] })
        return
      }

      // A failed probe is still HTTP 200 — a diagnostic outcome, not an error.
      setTestResults((prev) => ({
        ...prev,
        [connection.id]: { status: data.ok ? 'ok' : 'fail', message: data.message },
      }))
      clearResultSoon(connection.id)
    } catch (e) {
      setTestResults((prev) => ({
        ...prev,
        [connection.id]: { status: 'fail', message: apiErrorMessage(e, 'Network error.') },
      }))
      clearResultSoon(connection.id)
    }
  }

  // Destinations a swap may move properties ONTO: connections this business owns, minus the
  // one being replaced. A borrowed connection is never a destination (moving hotels onto one
  // would make this business depend on something it cannot manage).
  function destinationsFor(connection: PmsConnection): SwapCandidate[] {
    return connections
      .filter((candidate) => candidate.ownedByAccount && candidate.id !== connection.id)
      .map((candidate) => ({
        id: candidate.id,
        name: candidate.name,
        clientIdMasked: candidate.clientIdMasked,
        environment: candidate.environment,
        healthStatus: candidate.healthStatus,
        authBudget: candidate.authBudget,
      }))
  }

  return (
    <PortfolioLayout title="PMS connections" currentUser={currentUser}>
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Back to property — shown only when deep-linked from a property's sync banner. */}
        {fromProperty && (
          <Link
            href={fromProperty.basePath}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground animate-slide-in"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to {fromProperty.name}
          </Link>
        )}

        {/* Heading */}
        <section className="animate-slide-in" style={{ animationDelay: '0ms' }}>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">Business settings</h1>
            <HelpTooltip content={`The PMS credentials that connect your properties to ${brand}, and each property's latest sync activity.`} />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">Your PMS connections and the properties syncing through them.</p>
        </section>

        <AccountSettingsNav accountAbilities={accountAbilities} activeSection={activeSection} settingsPath={currentAccount.settingsPath} />

        {connections.length === 0 ? (
          <section className="animate-slide-in" style={{ animationDelay: '100ms' }}>
            <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
              <Plug className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No PMS connections yet</p>
              <p className="mt-1 text-xs text-muted-foreground">Connect your first property to set one up.</p>
              <Link
                href="/onboarding"
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-foreground px-4 py-2 text-xs font-medium text-white transition-colors hover:bg-foreground/90"
              >
                Add property
              </Link>
            </div>
          </section>
        ) : (
          connections.map((connection, index) => {
            const health = HEALTH_META[connection.healthStatus] ?? HEALTH_META.healthy
            const HealthIcon = health.Icon
            const test = testResults[connection.id]
            const healedMessage = healed[connection.id]

            // READ vs WRITE. Everyone who can manage connections may TEST any of them (they
            // depend on it); only the OWNING business may change one.
            const canWrite = canManageConnections && connection.ownedByAccount
            const borrowed = canManageConnections && !connection.ownedByAccount

            const budget = connection.authBudget
            // Only a READABLE meter at zero blocks. A null meter (Redis down) blocks nothing —
            // the server fails open too, and losing the meter must never block a recovery.
            const noChecksLeft = budgetExhausted(budget)
            const noChecksMessage = noChecksLeft && budget ? budgetExhaustedMessage(budget) : null

            const here = connection.properties
            const elsewhere = Math.max(connection.propertyCount - here.length, 0)
            const destinations = destinationsFor(connection)
            const canMove = canWrite && here.length > 0
            // Removable only when it serves NOTHING anywhere — propertyCount is GLOBAL.
            const canRemove = canWrite && connection.propertyCount === 0
            const needsAttention = connection.healthStatus !== 'healthy' && !healedMessage

            return (
              <section key={connection.id} className="animate-slide-in" style={{ animationDelay: `${100 + index * 50}ms` }}>
                <div
                  className={`overflow-hidden rounded-xl border bg-white shadow-sm ${
                    healedMessage ? 'border-emerald-200' : 'border-border/60'
                  }`}
                >
                  {/* Header: name + env + health badge; masked client id */}
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/40 px-5 py-4">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h2 className="text-sm font-semibold text-foreground">{connection.name}</h2>
                      {connection.environment === 'staging' ? (
                        <Badge variant="outline" className="border-amber-200 bg-amber-50 text-[11px] capitalize text-amber-700">
                          Staging
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-border bg-muted text-[11px] capitalize text-muted-foreground">
                          {connection.environment}
                        </Badge>
                      )}
                      <Badge variant="outline" className={`inline-flex items-center gap-1 text-[11px] ${health.className}`}>
                        <HealthIcon className="h-3 w-3" />
                        {health.label}
                      </Badge>
                      {!connection.ownedByAccount && (
                        <Badge
                          variant="outline"
                          className="inline-flex items-center gap-1 border-border bg-muted text-[11px] text-muted-foreground"
                        >
                          <Lock className="h-3 w-3" />
                          Managed elsewhere
                        </Badge>
                      )}
                    </div>
                    <span className="font-mono text-xs tabular-nums text-muted-foreground">{connection.clientIdMasked}</span>
                  </div>

                  {/* The resolution, when a test (or a new secret) just healed this connection. */}
                  {healedMessage && (
                    <ConnectionHealedPanel
                      message={healedMessage}
                      propertyNames={here.map((property) => property.name)}
                      onDismiss={() =>
                        setHealed((prev) => {
                          const next = { ...prev }
                          delete next[connection.id]
                          return next
                        })
                      }
                    />
                  )}

                  {/* What is wrong, who it hurts, and what actually fixes it. */}
                  {needsAttention && (
                    <ConnectionRecoveryPanel
                      tone={connection.healthStatus === 'suspended' ? 'suspended' : 'degraded'}
                      consecutiveAuthFailures={connection.consecutiveAuthFailures}
                      suspendedAt={connection.suspendedAt}
                      lastProbeAt={connection.lastProbeAt}
                      nextProbeEstimate={connection.nextProbeEstimate}
                      propertyNames={here.map((property) => property.name)}
                      canManage={canWrite}
                      canMove={canMove && destinations.length > 0}
                    />
                  )}

                  {/* A connection this business only borrows: testable, never editable. */}
                  {borrowed && (
                    <div className="flex items-start gap-2 border-b border-border/40 bg-muted/40 px-5 py-3 text-xs text-muted-foreground">
                      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        This connection is managed by another business — only they can change it. You can still test it
                        from here.
                      </span>
                    </div>
                  )}

                  {/* Management actions (owner/admin) */}
                  {canManageConnections && (
                    <div className="border-b border-border/40 px-5 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          variant={needsAttention ? 'default' : 'outline'}
                          size="sm"
                          className="h-8 rounded-lg text-xs"
                          onClick={() => testConnection(connection)}
                          disabled={test?.status === 'testing' || noChecksLeft}
                          aria-busy={test?.status === 'testing'}
                          title={noChecksMessage ?? undefined}
                        >
                          {test?.status === 'testing' ? (
                            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <ArrowRightLeft className="mr-1.5 h-3.5 w-3.5" />
                          )}
                          Test connection
                        </Button>

                        {canWrite && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 rounded-lg text-xs"
                            onClick={() => setReconnectTarget(connection)}
                            disabled={noChecksLeft}
                            title={noChecksMessage ?? undefined}
                          >
                            <KeyRound className="mr-1.5 h-3.5 w-3.5" />
                            Update secret
                          </Button>
                        )}

                        {canMove && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 rounded-lg text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => setSwapTarget(connection)}
                          >
                            <ArrowRightLeft className="mr-1.5 h-3.5 w-3.5" />
                            Move properties
                          </Button>
                        )}

                        {canRemove && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 rounded-lg text-xs text-rose-500 hover:text-rose-600"
                            onClick={() => setRemoveTarget(connection)}
                          >
                            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                            Remove connection
                          </Button>
                        )}

                        <ConnectionCheckMeter budget={budget} className="ml-auto" />
                      </div>

                      {/* Why the buttons are off — spelled out, never left to a disabled state. */}
                      {noChecksMessage && (
                        <p className="mt-2.5 flex items-start gap-1.5 text-xs text-amber-600">
                          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span>{noChecksMessage}</span>
                        </p>
                      )}

                      {/* Test outcome. Long enough to matter (a rate-limit refusal is a full
                          sentence), so it gets its own line rather than a truncated chip. */}
                      {test && test.status !== 'testing' && (
                        <p
                          role={test.status === 'ok' ? 'status' : 'alert'}
                          className={`mt-2.5 flex items-start gap-1.5 text-xs ${
                            test.status === 'ok' ? 'text-emerald-700' : 'text-rose-700'
                          }`}
                        >
                          {test.status === 'ok' ? (
                            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          ) : (
                            <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          )}
                          <span>{test.message || (test.status === 'ok' ? 'Connected to your PMS successfully.' : 'Test failed.')}</span>
                        </p>
                      )}
                    </div>
                  )}

                  {here.length === 0 ? (
                    <div className="px-5 py-4">
                      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                        <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                          {elsewhere > 0
                            ? `No properties from this business. It still serves ${elsewhere} ${elsewhere === 1 ? 'property' : 'properties'} in another business, so it can't be removed here.`
                            : canWrite
                              ? "This connection isn't serving any properties. You can remove it."
                              : "This connection isn't serving any properties."}
                        </span>
                      </p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Property</TableHead>
                          <TableHead className="text-right">Last structure sync</TableHead>
                          <TableHead className="text-right">Last bookings poll</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {here.map((property) => (
                          <React.Fragment key={property.id}>
                            <TableRow>
                              <TableCell>
                                <Link
                                  href={property.basePath}
                                  className="font-medium text-foreground transition-colors hover:text-primary"
                                >
                                  {property.name}
                                </Link>
                              </TableCell>
                              <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                                {formatShortWhen(property.lastStructureSyncAt)}
                              </TableCell>
                              <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                                {formatShortWhen(property.lastPolledAt)}
                              </TableCell>
                            </TableRow>
                            {property.errorMessage && (
                              <TableRow className="hover:bg-transparent">
                                <TableCell colSpan={3} className="bg-rose-50/60 py-2">
                                  <span className="flex items-start gap-1.5 text-xs text-rose-700">
                                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                    <span>
                                      {property.errorMessage}
                                      {property.errorAt && (
                                        <span className="ml-1.5 text-rose-500">({formatShortWhen(property.errorAt)})</span>
                                      )}
                                    </span>
                                  </span>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </section>
            )
          })
        )}
      </div>

      <ReconnectCredentialModal settingsPath={currentAccount.settingsPath}
        connection={
          reconnectTarget
            ? {
                id: reconnectTarget.id,
                name: reconnectTarget.name,
                clientIdMasked: reconnectTarget.clientIdMasked,
                environment: reconnectTarget.environment,
                authBudget: reconnectTarget.authBudget,
              }
            : null
        }
        onHealed={(connectionId) =>
          setHealed((prev) => ({ ...prev, [connectionId]: 'Connection restored. Your rate updates are resuming now.' }))
        }
        onClose={() => setReconnectTarget(null)}
      />
      <SwapCredentialModal settingsPath={currentAccount.settingsPath}
        connection={
          swapTarget
            ? { id: swapTarget.id, name: swapTarget.name, properties: swapTarget.properties.map((p) => ({ id: p.id, name: p.name })) }
            : null
        }
        candidates={swapTarget ? destinationsFor(swapTarget) : []}
        allowStagingEnvironment={allowStagingEnvironment}
        onClose={() => setSwapTarget(null)}
      />
      <RemoveConnectionDialog settingsPath={currentAccount.settingsPath}
        connection={
          removeTarget
            ? { id: removeTarget.id, name: removeTarget.name, clientIdMasked: removeTarget.clientIdMasked }
            : null
        }
        onClose={() => setRemoveTarget(null)}
      />
    </PortfolioLayout>
  )
}
